"use client"

/**
 * Client-side mirror of the previous Edge middleware. Same redirect rules as
 * `frontend/middleware.ts` (deleted), but evaluated in the browser so the app
 * works in a static export (Capacitor mobile build) where middleware is absent.
 *
 * Renders a full-page loader until the auth-storage bootstrap completes to
 * avoid a flash of unauthorized content.
 */
import { useEffect, useMemo } from "react"
import { usePathnameSafe } from "@/lib/hooks/use-pathname-safe"
import { jwtDecode } from "jwt-decode"
import { PageLoading } from "@/components/ui/feedback/page-loading"
import { useAuthSession } from "@/lib/auth/auth-provider"
import { authStorage } from "@/lib/auth/auth-storage"
import { useNavigate } from "@/lib/navigation"
import { IS_MOBILE_BUILD } from "@/lib/capacitor"

interface JwtPayload {
  sub?: string
  role?: string
  exp?: number
}

const MATCHED_PREFIXES = ["/login", "/fan", "/dashboard"]

/** Trailing slashes normalize away for consistent guard matching. */
function normalizePath(pathname: string): string {
  if (!pathname) return "/"
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1)
  }
  return pathname
}

function shouldGuard(normalizedPath: string): boolean {
  if (IS_MOBILE_BUILD && normalizedPath === "/") return true
  return MATCHED_PREFIXES.some(
    (prefix) =>
      normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  )
}

/** Loader copy during token bootstrap — align with destination route (see dashboard home flow). */
function bootstrapPageTitle(normalizedPath: string): string {
  if (normalizedPath.startsWith("/dashboard")) return "Dashboard"
  if (normalizedPath.startsWith("/fan")) return "Fan"
  return "Session"
}

function decodeToken(token: string | null): JwtPayload | null {
  if (!token) return null
  try {
    return jwtDecode<JwtPayload>(token)
  } catch {
    return null
  }
}

function isExpired(payload: JwtPayload | null): boolean {
  if (!payload?.exp) return false
  return Date.now() / 1000 > payload.exp
}

function resolveRedirect(
  normalizedPath: string,
  payload: JwtPayload | null
): string | null {
  // Mobile opens at `/` (marketing landing is web-only): send users to login or home.
  if (IS_MOBILE_BUILD && normalizedPath === "/") {
    if (!payload || isExpired(payload)) return "/login"
    return payload.role === "fan" ? "/fan/players" : "/dashboard"
  }

  // /login: bounce authenticated users to their home.
  if (normalizedPath === "/login") {
    if (!payload || isExpired(payload)) return null
    return payload.role === "fan" ? "/fan/players" : "/dashboard"
  }

  // /fan/* (excluding /fan itself): require fan role.
  if (
    normalizedPath.startsWith("/fan/") ||
    (normalizedPath.startsWith("/fan") && normalizedPath !== "/fan")
  ) {
    if (!payload || isExpired(payload) || payload.role !== "fan") return "/fan"
    return null
  }

  // /dashboard*: require non-fan token.
  if (normalizedPath.startsWith("/dashboard")) {
    if (!payload || isExpired(payload)) return "/login"
    if (payload.role === "fan") return "/fan"
    return null
  }

  return null
}

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isReady, hasToken } = useAuthSession()
  const navigate = useNavigate()
  const pathname = usePathnameSafe()
  const normalizedPath = normalizePath(pathname)

  // Recompute when token changes (login/logout cause hasToken to flip).
  const { redirectTo, needsRedirect } = useMemo(() => {
    if (!isReady) return { redirectTo: null as string | null, needsRedirect: false }
    if (!shouldGuard(normalizedPath)) {
      return { redirectTo: null, needsRedirect: false }
    }
    const payload = decodeToken(authStorage.getToken())
    const to = resolveRedirect(normalizedPath, payload)
    const needs = !!(to && normalizePath(to) !== normalizedPath)
    return { redirectTo: to, needsRedirect: needs }
  }, [isReady, hasToken, normalizedPath])

  useEffect(() => {
    if (needsRedirect && redirectTo) {
      navigate(redirectTo, { replace: true })
    }
  }, [needsRedirect, redirectTo, navigate])

  // Only block rendering for guarded routes. Public routes (/, /about, etc.)
  // render immediately to avoid an unnecessary loading flash. On mobile, `/`
  // is guarded and waits for persisted token bootstrap.
  if (!isReady && shouldGuard(normalizedPath)) {
    return (
      <div className="flex min-h-dvh min-h-screen flex-col bg-background">
        <PageLoading pageTitle={bootstrapPageTitle(normalizedPath)} />
      </div>
    )
  }

  // While a redirect is queued, render nothing to avoid a flash of the
  // not-yet-authorized page.
  if (needsRedirect) {
    return null
  }

  return <>{children}</>
}
